import { h, Component } from 'preact';
import { accessNested, parseDate } from 'utils/utils';
import { getCopyWithParams } from 'utils/clientUtils';
import pure from 'utils/pure';
import Geo from 'models/Geo';
import Store from 'store/store';
import HTML from 'components/core/html/html';
import { track } from 'utils/tracking';
import { Link } from 'components/core/link/link.js';
import Theater from 'components/shared/theaters/theater';
import MessengerButton from 'components/shared/social/messengerButton';
import CalendarRemind from 'components/shared/calendarRemind/calendarRemind';
import Loading from 'components/shared/misc/loading';
import Youtube from 'components/shared/youtube/youtube.js';
import Kaltura from 'components/shared/kaltura/kaltura.js';
import s from 'components/shared/theaters/theaters.sss';
import Vimeo from 'components/shared/vimeo/vimeo.js';

export default @pure class Theaters extends Component {

	constructor() {
		super();

		this.renderSearchFailedError = this.renderSearchFailedError.bind(this);
		this.renderGeocodingError    = this.renderGeocodingError.bind(this);

		this.state = {
			firstLoad: true,
			selectedDate: null
		};

		this.noShowtimesTracked = null;
	}

	// Finds one element that matches the current client geo or returns undefined if no match
	matchingGeo(elements) {
		if (!elements) return undefined;
		let geo = accessNested(this.props, 'client.geo');
		// TODO: sort object by order
		return Object.keys(elements).map(e => elements[e]).find(val => {
			if (!val || typeof val !== 'object') return false;
			if (geo && val.location) {
				let location = typeof val.location === 'string' ? val.location.split(',').map(e => +e.trim()) : [+val.location.lat, +val.location.lon];
				let lGeo = new Geo({ lat: location[0], lon: location[1] });
				let dist = lGeo.distanceFrom(geo);
				let maxDist = +val.radius || 50;
				if (dist == undefined || dist > maxDist) {
					return false;
				}
			}
			if (geo && val.country) {
				if (val.country !== geo.countryCode) {
					return false;
				}
			}
			return true;
		});
	}

	noShowtimesMessage() {
		const { appData, copy, client, pageData = {}, showtimes = {} } = this.props;
		let version = showtimes.version || Date.now();
		if (this.noShowtimesTracked !== version && typeof window !== 'undefined') {
			this.noShowtimesTracked = version;
			// Note: we should not track in a render function
			track({ a: 'no_showtimes_available', ni: true });
		}

		const geo = client.geo && (client.geo.src || client.geo.city) || '';
		let copyCity = geo ? '_' + geo.toUpperCase() : '';
		copyCity = copyCity.split(',')[0]; //remove the state / county from the search
		copyCity = copyCity.replace(/ /g, '_'); // REPLACE SPACES WITH UNDERSCORES
		let copyTitle;
		let copyVal;

		let geoMessage = this.matchingGeo(pageData.noShowtimesMessage);
		if (geoMessage) {
			copyTitle = copy[geoMessage.title] || geoMessage.title;
			copyVal = copy[geoMessage.text] || geoMessage.text;
		}

		copyTitle = copyTitle || copy['$NO_SHOWTIMES' + copyCity] || copy.$NO_SHOWTIMES;
		copyVal = copyVal || copy['$NO_SHOWTIMES_DESCRIPTION' + copyCity] || copy.$NO_SHOWTIMES_DESCRIPTION;

		const title           = getCopyWithParams({ LOCATION: geo, GEO: geo }, copyTitle || '');
		const description     = getCopyWithParams({ LOCATION: geo, GEO: geo }, copyVal || '');
		const noShowtimesLink = this.renderNoShowtimesLink(pageData.noShowtimesLink);

		let messengerElem = null;
		if (appData.options.useMessengerRemind) {
			const messenger = accessNested(appData, 'meta.messenger', {});
			const appId = accessNested(appData, 'meta.id', null);
			messengerElem = [
				<MessengerButton copy={copy} geo={client.geo} appId={appId} fbAppId={messenger.appId} fbPageId={messenger.pageId} />,
				<span class={s.or}>{copy.$OR}</span>
			];
		}

		return (
			<div class={s.noShowtimes} key="noshowtimes">
				<HTML class={s.title} content={title} markdown />
				<HTML class={s.description} content={description} markdown />
				{messengerElem}
				{noShowtimesLink}
				{this.renderNoShowtimesCalendar()}
				{this.renderNoShowtimesVideo()}
			</div>
		);
	}

	renderNoShowtimesVideo() {
		const pageData = this.props.pageData || {};
		let noShowtimesVideo = pageData.noShowtimesVideo;

		if (!noShowtimesVideo) {
			// No override specified, so let's try and grab one from the videos page
			const videoList = accessNested(this.props, 'appData.pages.videos.data.list', {});
			noShowtimesVideo = Object.values(videoList).find(v => v); // take the first video available
		}

		if (typeof noShowtimesVideo === 'string') {
			noShowtimesVideo = {
				ytid: noShowtimesVideo
			};
		}

		if (!noShowtimesVideo) {
			return;
		}
		if (noShowtimesVideo.ytid || noShowtimesVideo.playlistId) {
			return (
				<div class={s.trailer} key="no-showtimes-video-youtube">
					<div class={s.cta}>{this.props.copy['$OR_WATCH_TRAILER']}</div>
					<Youtube
						appData={this.props.appData}
						activeItem={noShowtimesVideo}
						maintainAspectRatio
						autoplay={false}
						wrapperClass={s.youtube}
						client={this.props.client}
					/>
				</div>
			);
		} else if (noShowtimesVideo.vimeoID) {
			return (
				<Vimeo
					class={s.vimeoPlayer}
					vimeoID={noShowtimesVideo.vimeoID}
					maintainAspectRatio
					key="no-showtimes-video-vimeo"
				/>
			);
		} else if (noShowtimesVideo.disneySrc) {
			return (
				<div class={s.trailer}>
					<div class={s.cta}>{this.props.copy['$OR_WATCH_TRAILER']}</div>
					<Kaltura
						src={noShowtimesVideo.disneySrc}
					/>
				</div>
			);
		}
	}

	renderNoShowtimesLink(link) {
		if (!link) return;
		let linkProps = ['url', 'text', 'trackId', 'trackLabel'];
		// Clean link
		let base = linkProps.reduce((base, prop) => {
			base[prop] = link[prop];
			return base;
		}, {});

		let match = this.matchingGeo(link);

		let result = Object.assign(base, match);

		if (!result.url || !result.text) return;

		const trackId    = result.trackId;
		const trackLabel = result.trackLabel;
		const linkHref   = result.url;
		const linkText   = result.text;

		const linkAttributes = {};
		if (this.props.appData.pages[linkHref]) {
			linkAttributes.pageId = linkHref;
		} else {
			linkAttributes.href = linkHref;
			linkAttributes.target = '_blank';
		}

		return (
			<Link
				trackId={trackId}
				trackLabel={trackLabel}
				class={s.noShowtimesLink}
				{...linkAttributes}
			>
				{linkText}
			</Link>
		);
	}

	renderNoShowtimesCalendar() {
		const p = this.props;
		const appData = p.appData;
		const meta = appData.meta;

		let releaseDate = appData.options.useBoxOfficeCalendar && meta.dates.boxOffice ? meta.dates.boxOffice : meta.dates.release;
		releaseDate = parseDate(releaseDate);

		const forceShowtimesCalendar = appData.options.forceShowtimesCalendar;
		if (!forceShowtimesCalendar && Date.now() > releaseDate) return;

		return (
			<CalendarRemind
				key="showtimesRemind"
				pageData={p.pageData}
				copy={p.copy}
				appUrl={p.appData.meta.url}
				releaseDate={releaseDate}
				sharedImageRoot={p.appData.urls.roots.sharedImages}
				useReveal={!p.pageData.useCalendarDropdown}
				client={p.client}
				activePage={p.activePage}
			/>
		);
	}

	renderBoundingRegionError(props) {
		const {
			copy,      // (object) containing all of the $COPY variables for the app
			client,    // (object) containing viewport and geolocation info about the client
			pageData,  // (object) containg the pageData from the showtimes
			appData    // (object)
		} = props;

		let geo = client.geo || {};

		let appRegion = accessNested(appData, 'meta.region') || {};

		const {
			noShowtimesLink,                     // (object) containing label, url, tracking etc for the now showtimes link
			showNoShowtimesLinkWorldwide = false // (boolean) whether to render the no showtimes link on bounding-region errors
		} = pageData;

		// get region data
		const location           = geo.src || geo.city || '';
		const region             = (appRegion.slug || '').toUpperCase();
		const country            = (appRegion.countryName || '').toUpperCase() || region;
		const normalisedLocation = location.toUpperCase().replace(/ /g, '_');

		// composite error based on mobile / desktop / location
		const error              = '$SEARCHED_LOCATION_OUTSIDE_REGION';
		const desktopError       = copy[`${error}_${normalisedLocation}`] || copy[error] || '';
		const mobileError        = copy[`${error}_MOBILE_${normalisedLocation}`] || copy[`${error}_MOBILE`];
		const boundingError      = (client.deviceSize === 'mobile' && mobileError) || desktopError;
		const boundingErrorCopy  = getCopyWithParams({ LOCATION: location, REGION: region, COUNTRY: country }, boundingError);
		const boundingErrorText  = boundingErrorCopy;

		const link = showNoShowtimesLinkWorldwide && this.renderNoShowtimesLink(noShowtimesLink);

		return (
			<div class={s.noShowtimes} key="noshowtimes">
				<HTML
					class={s.descriptionBounding}
					content={boundingErrorText}
					markdown
					key="message"
				/>
				{link}
				{this.renderNoShowtimesVideo()}
			</div>
		);
	}

	renderSearchFailedError(props) {
		const {
			copy,      // (object) containing all of the $COPY variables for the app
			client     // (object) containing viewport and geolocation info about the client
		} = props;

		const {
			src  = '', // (string) the location entered by the user in the location search
			city = ''  // (string) nearest city to the user's physical location
		} = client.geo;

		const location           = src || city;
		const normalisedLocation = location.toUpperCase().replace(/ /g, '_');
		const cityFailedCopy     = copy[`$SEARCH_FAILED_${normalisedLocation}`];
		const cityUnknownText    = copy[`$UNKNOW_ERROR_${normalisedLocation}`] || copy.$UNKNOW_ERROR;
		const searchFailedCopy   = getCopyWithParams({ LOCATION: location }, cityFailedCopy || copy.$SEARCH_FAILED || '');
		const searchFailedText   = searchFailedCopy;
		const video              = this.renderNoShowtimesVideo();

		return(
			<div class={s.noShowtimes} key="noshowtimes">
				<div class={s.title}>
					{cityUnknownText}
				</div>
				<HTML
					class={s.description}
					content={searchFailedText}
					markdown
				/>
				{video}
			</div>
		);
	}

	renderGeocodingError(props) {

		const {
			copy,      // (object) containing all of the $COPY variables for the app
			client     // (object) containing viewport and geolocation info about the client
		} = props;

		const {
			src  = '', // (string) the location entered by the user in the location search
			city = ''  // (string) nearest city to the user's physical location
		} = client.geo || {};


		const location              = src || city;
		const incorrectSpellingCopy = copy[`$CHECK_SEARCH_SPELLED_CORRECTLY_${location}`] || copy.$CHECK_SEARCH_SPELLED_CORRECTLY || '';
		const locationErrorText     = copy[`$UNABLE_TO_FIND_LOCATION_${location}`] || copy.$UNABLE_TO_FIND_LOCATION;
		const errorText             = incorrectSpellingCopy;
		const video                 = this.renderNoShowtimesVideo();

		return(
			<div class={s.noShowtimes} key='noshowtimes'>
				<div class={s.title}>
					{locationErrorText}
				</div>
				<HTML
					class={s.description}
					content={errorText}
					markdown
				/>
				{video}
			</div>
		);
	}

	render(props) {
		const {
			theaters,
			selectedDate,
			selectedProvider,
			filters,
			appData,
			client,
			// groupState,
			// group,
			// favorites,
			copy,
			loading,
			// showtimesErr,
			pageData = {},
			ticketing,
			showHiddenExhibitors,
			addressClickOpensMap
		} = props;

		if (loading) {
			return <Loading />;
		}

		if (!theaters || !theaters.length) {

			// 09/01/20: prevent executing noShowtimesMessage and renderNoShowtimesCalendar;
			const showtimesErr = accessNested(Store.get(), 'showtimes.error');

			if (showtimesErr) {
				// TODO: redo that (duplicated code)
				switch (showtimesErr) {
					case 'BOUNDING_REGION':
						return this.renderBoundingRegionError(props);
					case 'GEOCODING':
						return this.renderGeocodingError(props);
					default:
						// eslint-disable-next-line no-console
						console.log('UNKNOWN ERROR:', showtimesErr);
						return this.renderSearchFailedError(props);
				}
			}
			// No error : we just don't have any result
			return this.noShowtimesMessage();
		}

		const hideFilteredTheaters = accessNested(pageData, 'hideFilteredTheaters');
		let previous;
		const renderedTheaters = theaters.reduce((a, t, i) => {
			// if(!t.enabled || !t.activeExhibitor) return a;
			if (!t.enabled || (!showHiddenExhibitors && !t.activeExhibitor)) return a;
			if (!t.activeScreenings.length) return a;
			if (!t.filterPass && hideFilteredTheaters) return a;

			if (previous && !t.pinned && previous.pinned && pageData.underPinCta) {
				let message = copy[pageData.underPinCta] || pageData.underPinCta;
				a.push(<HTML class={s.underPinCta} content={message} markdown />);
			}

			// prevFaved = !(prevFaved === false) && isFaved;
			const theaterComp = (
				<Theater
					theater={t}
					customLogo={pageData.customTheaterLogo}
					index= {i}
					key={t.id}
					client={client}
					selectedDate={t.active && selectedDate}
					selectedProvider={t.active && selectedProvider}
					filters={filters}
					appData={appData}
					pageData={pageData}
					ticketing={ticketing}
					addressClickOpensMap={addressClickOpensMap}
					preventScrollToTheater={props.preventScrollToTheater}
				/>
			);
			a.push(theaterComp);

			previous = t;
			return a;
		}, []);

		if (!renderedTheaters.length) {
			return this.noShowtimesMessage();
		}
		return <ul class={s.theatersList}>{renderedTheaters}</ul>;
	}

}
